import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import Logo from './Logo';

type Backflip = typeof import('./logo-backflip');
type BackflipLoader = () => Promise<Backflip>;

const loadBackflip: BackflipLoader = () => import('./logo-backflip');

interface AnimatedLogoProps {
  trigger?: 'interaction' | 'page-end';
  backflipLoader?: BackflipLoader;
  className?: string;
  color?: string;
}

export default function AnimatedLogo({
  trigger = 'interaction',
  backflipLoader = loadBackflip,
  className = 'w-30 lg:w-46 shrink-0',
  color = 'text-green-50',
}: AnimatedLogoProps) {
  const [frame, setFrame] = useState<number | null>(null);
  const backflip = useRef<Backflip | null>(null);
  const loading = useRef<Promise<Backflip> | null>(null);
  const pending = useRef(false);
  const hovered = useRef(false);
  const focused = useRef(false);
  const raf = useRef<number | null>(null);
  const run = useRef(0);
  const logo = useRef<HTMLSpanElement>(null);
  const artwork = useRef<SVGSVGElement>(null);
  const { pathname } = useLocation();

  const stop = useCallback(() => {
    run.current++;
    pending.current = false;
    if (raf.current !== null) cancelAnimationFrame(raf.current);
    raf.current = null;
    setFrame(null);
  }, []);

  const cancelAbandonedInteraction = useCallback(() => {
    if (pending.current && !hovered.current && !focused.current) stop();
  }, [stop]);

  const start = useCallback((module: Backflip, currentRun: number) => {
    if (raf.current !== null) cancelAnimationFrame(raf.current);
    const started = performance.now();
    setFrame(module.firstFrame);
    function tick(now: number) {
      if (run.current !== currentRun) return;
      const next = Math.min(
        module.lastFrame,
        module.firstFrame + ((now - started) * module.framesPerSecond) / 1000,
      );
      setFrame(next < module.lastFrame ? next : null);
      raf.current =
        next < module.lastFrame ? requestAnimationFrame(tick) : null;
    }
    raf.current = requestAnimationFrame(tick);
  }, []);

  const play = useCallback(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const currentRun = ++run.current;
    if (!backflip.current) {
      pending.current = true;
      const request = loading.current ?? backflipLoader();
      loading.current = request;
      void request.then(
        (module) => {
          backflip.current = module;
          if (run.current !== currentRun) return;
          pending.current = false;
          if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches)
            start(module, currentRun);
        },
        () => {
          if (loading.current === request) loading.current = null;
          if (run.current === currentRun) pending.current = false;
        },
      );
      return;
    }
    pending.current = false;
    start(backflip.current, currentRun);
  }, [backflipLoader, start]);

  useLayoutEffect(() => {
    const svg = artwork.current;
    if (!svg) return;
    // Measure the unshifted pose before paint, never the previous correction.
    // Only the header artwork may move: its 64px row, letters, and page flow
    // stay fixed, and the footer always keeps the full authored trajectory.
    svg.style.transform = '';
    if (trigger !== 'interaction' || frame === null) return;

    let top = Infinity;
    for (const path of svg.querySelectorAll('path')) {
      if (path.getAttribute('opacity') === '0') continue;
      let strokeRadius = 0;
      const strokeWidth = Number(path.getAttribute('stroke-width'));
      if (strokeWidth) {
        const matrix = path.getScreenCTM();
        // getBoundingClientRect excludes SVG strokes. Account for their
        // vertical extent after all authored rotations/scales, not just fill.
        if (matrix)
          strokeRadius = (strokeWidth * Math.hypot(matrix.b, matrix.d)) / 2;
      }
      top = Math.min(top, path.getBoundingClientRect().top - strokeRadius);
    }
    const offset = Math.max(0, (window.visualViewport?.offsetTop ?? 0) - top);
    if (offset > 0) svg.style.transform = `translateY(${offset}px)`;
  }, [frame, trigger]);

  useEffect(() => {
    const link = logo.current?.closest('a');
    const hoverTarget = link ?? logo.current;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const runRef = run;
    function onFocus() {
      focused.current = true;
      play();
    }
    function onBlur() {
      focused.current = false;
      cancelAbandonedInteraction();
    }
    function onMouseLeave() {
      hovered.current = false;
      cancelAbandonedInteraction();
    }
    function onMotionChange() {
      if (!motion.matches) return;
      stop();
    }
    if (trigger === 'interaction') {
      link?.addEventListener('focus', onFocus);
      link?.addEventListener('blur', onBlur);
      hoverTarget?.addEventListener('mouseleave', onMouseLeave);
    }
    motion.addEventListener('change', onMotionChange);
    return () => {
      if (trigger === 'interaction') {
        link?.removeEventListener('focus', onFocus);
        link?.removeEventListener('blur', onBlur);
        hoverTarget?.removeEventListener('mouseleave', onMouseLeave);
      }
      hovered.current = false;
      focused.current = false;
      motion.removeEventListener('change', onMotionChange);
      runRef.current++;
      pending.current = false;
      if (raf.current !== null) cancelAnimationFrame(raf.current);
      raf.current = null;
    };
  }, [trigger, play, stop, cancelAbandonedInteraction]);

  useEffect(() => {
    if (trigger !== 'page-end') return;

    let armed = true;
    const element = logo.current;
    const footer = element?.closest('footer');
    const runRef = run;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    function checkEnd() {
      if (!footer) return;
      // The footer has bottom padding after the logo. Its appearance alone
      // must not start the animation before the actual end is reached.
      const distanceFromEnd = Math.max(
        document.documentElement.scrollHeight -
          window.scrollY -
          window.innerHeight,
        footer.getBoundingClientRect().bottom - window.innerHeight,
      );
      // Ignore sub-pixel/very small layout jitter, but rearm on a short scroll away.
      if (distanceFromEnd > 8) {
        armed = true;
        // An in-flight import is not permission to play after leaving the end.
        if (pending.current) stop();
      }
      if (armed && distanceFromEnd <= 1 && !motion.matches) {
        armed = false;
        play();
      }
    }
    const observer = new ResizeObserver(checkEnd);
    observer.observe(document.documentElement);
    observer.observe(document.body);
    window.addEventListener('scroll', checkEnd, { passive: true });
    window.addEventListener('resize', checkEnd);
    motion.addEventListener('change', checkEnd);
    // Defer initial/restored position until after navigation's scroll restoration.
    const initialCheck = requestAnimationFrame(checkEnd);
    return () => {
      cancelAnimationFrame(initialCheck);
      observer.disconnect();
      window.removeEventListener('scroll', checkEnd);
      window.removeEventListener('resize', checkEnd);
      motion.removeEventListener('change', checkEnd);
      runRef.current++;
      pending.current = false;
      if (raf.current !== null) cancelAnimationFrame(raf.current);
      raf.current = null;
      setFrame(null);
    };
  }, [trigger, pathname, play, stop]);

  const module = backflip.current;
  const showFigure = frame !== null && !!module?.figureVisibleAt(frame);
  const silhouette = showFigure ? module!.silhouettePathAt(frame!) : null;

  return (
    <span
      ref={logo}
      className={`${className} ${color} relative block h-auto`}
      onMouseEnter={
        trigger === 'interaction'
          ? () => {
              hovered.current = true;
              play();
            }
          : undefined
      }
    >
      <Logo className="block h-auto w-full" hideR={showFigure} />
      <svg
        ref={artwork}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-auto w-full overflow-visible"
        viewBox="0 0 208 34"
        fill="currentColor"
      >
        {silhouette && <path d={silhouette} />}
        {showFigure && !silhouette && (
          <g transform={module!.figureTransform}>
            {module!.drawingsAt(frame!).map((drawing, index) => (
              <path
                key={index}
                d={drawing.d}
                transform={drawing.transform}
                opacity={drawing.opacity}
                fill={drawing.stroke ? 'none' : 'currentColor'}
                stroke={drawing.stroke ? 'currentColor' : undefined}
                strokeWidth={drawing.strokeWidth}
                strokeLinecap={drawing.lineCap}
                strokeLinejoin={drawing.lineJoin}
              />
            ))}
          </g>
        )}
      </svg>
    </span>
  );
}
